'use client';

import { Button } from '@ophelos/ui/components/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ophelos/ui/components/tabs';
import { CreditCard, NotebookPen, ScrollText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { DebtPageHeader } from '@/components/layout/DebtPageHeader';
import { soleCreditor } from '@/lib/creditor';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useAuth } from '@/contexts/customer-auth-context';
import { useTranslation } from '@/hooks/useTranslation';
import { Loader } from '@ophelos/ui/components/loader';
import { ACCOUNT_ENTRY_ID, buildAccountEntry, debtNameByTransaction } from '@/lib/accountPayment';
import { transactionPlanId } from '@/lib/paymentPlans';
import { PaymentPlanOverview } from './_partials/_paymentPlanPartials/PaymentPlanOverview';
import { DebtBreakdownTab } from './_partials/_shared/DebtBreakdownTab';
import { filterCustomerTransactions, TransactionsList } from './_partials/_shared/TransactionsList';
import type { OphelosPaymentPlanResponse, PaymentPlanData } from '@/types/payment-plan';
import { Empty, EmptyMedia, EmptyHeader, EmptyTitle } from '@ophelos/ui/components/empty';

function localDateString(d: Date = new Date()): string {
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
}

/** Cents-rounded total of a set of instalments. */
function scheduleTotal(items: OphelosPaymentPlanResponse['schedule']): number {
  return Math.round(items.reduce((total, item) => total + item.subunits / 100, 0) * 100) / 100;
}

/**
 * Transform Ophelos payment plan response to UI-friendly format.
 *
 * Every figure is the plan's own: this page answers "how is my plan going?", and the debt balance
 * moves with activity the plan never collected — mixing the two makes the card disagree with itself.
 */
export function transformPaymentPlanData(ophelosData: OphelosPaymentPlanResponse): PaymentPlanData {
  const schedule = ophelosData.schedule;
  const totalPayments = schedule.length;

  // Count completed (succeeded) payments
  const completedPayments = schedule.filter((item) => item.status === 'succeeded').length;

  const planTotal = scheduleTotal(schedule);
  const totalPaid = scheduleTotal(schedule.filter((item) => item.status === 'succeeded'));

  // Find next actionable payment (first installment that is not succeeded)
  const nextPayment = schedule.find((item) => item.status !== 'succeeded');

  // Calculate frequency from schedule dates
  let frequency = 'Monthly';
  if (schedule.length >= 2) {
    const date1 = new Date(schedule[0].date);
    const date2 = new Date(schedule[1].date);
    const daysDiff = Math.abs((date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff <= 7) frequency = 'Weekly';
    else if (daysDiff <= 16) frequency = 'Bi-Weekly';
    else if (daysDiff <= 32) frequency = 'Monthly';
    else frequency = 'Custom';
  }

  // Payment amount from first scheduled payment (convert subunits to currency units)
  const paymentAmount = schedule[0] ? schedule[0].subunits / 100 : 0;

  return {
    frequency,
    startedOn: schedule[0]?.date || localDateString(),
    paymentAmount,
    nextPaymentDate: nextPayment?.date || localDateString(),
    completedPayments,
    totalPayments,
    originalBalance: planTotal,
    currentBalance: Math.round((planTotal - totalPaid) * 100) / 100,
    totalPaid,
  };
}

/**
 * Generate chart data from Ophelos payment plan schedule
 * Shows completed vs predicted balance over time
 */
export function generateChartData(ophelosData: OphelosPaymentPlanResponse) {
  const schedule = ophelosData.schedule;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const chartData = [];

  // The line tracks this plan, so it starts at what the plan was drawn up to collect. Deriving it
  // from the debt balance instead lets activity the plan never collected rewrite its history.
  let runningBalance = scheduleTotal(schedule);

  if (schedule.length > 0) {
    const firstPaymentDate = new Date(schedule[0].date);
    // Get the month before the first payment, or use "Start" label
    const startMonth = firstPaymentDate.getMonth() === 0 ? 11 : firstPaymentDate.getMonth() - 1;
    chartData.push({ month: months[startMonth], balance: runningBalance, predicted: runningBalance });
  }

  for (let i = 0; i < schedule.length; i++) {
    const item = schedule[i];
    const paymentDate = new Date(item.date);
    const monthLabel = months[paymentDate.getMonth()];
    const paymentAmount = item.subunits / 100;

    // The schedule and the balance come from different sources, so instalments can total a
    // cent or two over the debt. Rounding to cents also absorbs float drift, which would
    // otherwise leave a final balance of ~1e-14 instead of zero. A balance is never negative,
    // and any point below zero makes recharts pad the axis with a negative tick.
    const remaining = Math.max(0, Math.round((runningBalance - paymentAmount) * 100) / 100);

    if (item.status === 'succeeded') {
      runningBalance = remaining;
      chartData.push({ month: monthLabel, balance: runningBalance, predicted: runningBalance });
    } else {
      // Future/pending payments show only predicted balance
      chartData.push({ month: monthLabel, predicted: remaining });
      runningBalance = remaining;
    }
  }

  return chartData;
}

export default function PaymentPlan() {
  const { debtId } = useParams<{ debtId: string }>();
  const navigate = useNavigate();
  const { isPreviewMode, isLoading: configLoading } = useConfiguration();
  const { t } = useTranslation();
  const { accountId } = useAuth();
  const { debts, accountPaymentPlan, isLoading } = useDebtData();
  const [activeTab, setActiveTab] = useState('overview');

  // Wait for configuration to load before rendering
  if (configLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader fullScreen />
        </div>
      </Layout>
    );
  }

  // Find debt from cached data (includes fullPlan from AccountView). An account
  // plan belongs to no case, so there is no case id to look it up by.
  const debt = useMemo(() => {
    if (!debtId) return null;
    if (debtId === ACCOUNT_ENTRY_ID) {
      return buildAccountEntry(accountPaymentPlan, debts, (count) => t('dashboard.coversDebts', { count }));
    }
    return debts.find((d) => d.id === debtId) || null;
  }, [debts, debtId, accountPaymentPlan, t]);

  // An account plan's synthetic entry has no creditor of its own; brand it with its members' when they share one.
  const creditor = debtId === ACCOUNT_ENTRY_ID ? soleCreditor(debts.filter((d) => d.isAccountPlanMember)) : debt;

  // Get payment plan data from cached fullPlan
  const paymentPlanData = useMemo(() => {
    return debt?.paymentPlan?.fullPlan || null;
  }, [debt]);

  const paymentPlanError = useMemo(() => {
    if (!debt) return null;
    if (!debt.paymentPlan) return t('paymentPlan.errorNoPlanFound');
    if (!debt.paymentPlan.fullPlan) return t('paymentPlan.errorDetailsNotAvailable');
    return null;
  }, [debt, t]);

  const isLoadingPaymentPlan = isLoading;

  // Redirect if not authenticated or no debtId
  useEffect(() => {
    if (!accountId && !isPreviewMode) {
      navigate('/login');
      return;
    }

    if (!debtId) {
      navigate(isPreviewMode ? '/dashboard?preview=true' : '/dashboard');
      return;
    }
  }, [accountId, debtId, navigate, isPreviewMode]);

  if (isLoading) {
    return (
      <Layout showFooter={false}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader fullScreen />
        </div>
      </Layout>
    );
  }

  if (!debt) {
    return (
      <Layout showFooter={false}>
        <Breadcrumb />
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-destructive">{t('payment.debtNotFound')}</p>
        </div>
      </Layout>
    );
  }

  // Show loading state while fetching payment plan
  if (isLoadingPaymentPlan) {
    return (
      <Layout showFooter={false}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader fullScreen />
        </div>
      </Layout>
    );
  }

  // Show error if payment plan fetch failed or no payment plan exists
  if (paymentPlanError || !paymentPlanData) {
    return (
      <Layout showFooter={false}>
        <Breadcrumb />

        <div className="flex h-screen items-center justify-center p-4">
          <div className="w-full max-w-md">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon" className="mt-6 mb-8">
                  <div className="p-4.5 border  border-border/30 rounded-[3rem]">
                    <div className="p-4 border border-border/60 rounded-4xl">
                      <div className="p-3 border rounded-2xl">
                        <NotebookPen />
                      </div>
                    </div>
                  </div>
                </EmptyMedia>
                <EmptyTitle className="text-center mb-4">{paymentPlanError || t('paymentPlan.noPlanFound')}</EmptyTitle>
                <Button asChild>
                  <Link to={isPreviewMode ? '/dashboard?preview=true' : '/dashboard'}>{t('common.backToDashboard')}</Link>
                </Button>
              </EmptyHeader>
            </Empty>
          </div>
        </div>
      </Layout>
    );
  }

  // Derive UI-friendly data and chart data from Ophelos data
  const uiPaymentPlanData = transformPaymentPlanData(paymentPlanData);
  const chartData = generateChartData(paymentPlanData);
  const externalPaymentPlanId = debt.paymentPlan?.externalPaymentPlanId;

  // An account entry's transactions span every covered case, so they include payments made outside
  // this plan. A case owns its payments outright, so its list stays whole.
  const isAccountEntry = debtId === ACCOUNT_ENTRY_ID;
  const customerTransactions = filterCustomerTransactions(debt.transactions).filter(
    (tx) => !isAccountEntry || transactionPlanId(tx) === paymentPlanData.id,
  );
  const debtNames = isAccountEntry ? debtNameByTransaction(debts.filter((d) => d.isAccountPlanMember)) : undefined;

  return (
    <Layout>
      {/* <Breadcrumb /> */}

      <div className="mx-auto flex-1 h-full w-full">
        <Tabs variant="default" value={activeTab} onValueChange={setActiveTab} className="space-y-2">
          <DebtPageHeader backTo={isPreviewMode ? '/dashboard?preview=true' : '/dashboard'} creditor={creditor}>
            <TabsList className="max-w-lg w-full mx-auto max-sm:h-auto">
              <TabsTrigger value="overview" className="rounded-full max-xs:text-xs">
                <CreditCard className="size-4 max-xs:hidden text-muted-foreground" />
                {t('payment.plan')}
              </TabsTrigger>
              <TabsTrigger value="breakdown" className="rounded-full max-xs:text-xs">
                <ScrollText className="size-4 max-xs:hidden text-muted-foreground" />
                {t('payment.details')}
              </TabsTrigger>
            </TabsList>
          </DebtPageHeader>

          <TabsContent value="overview" className="max-w-4xl w-full mx-auto">
            <PaymentPlanOverview
              paymentPlanData={uiPaymentPlanData}
              chartData={chartData}
              paymentPlanId={externalPaymentPlanId}
              rawPlanData={paymentPlanData}
            />
          </TabsContent>

          <TabsContent value="breakdown">
            <DebtBreakdownTab
              debt={debt}
              history={
                <TransactionsList
                  transactions={customerTransactions}
                  title={t('transaction.transactions')}
                  currency={paymentPlanData.currency}
                  debtNames={debtNames}
                />
              }
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
